import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Row,
  Skeleton,
  Space,
  Tabs,
  Tag,
  Typography,
} from 'antd';
import {
  CheckCircleFilled,
  MedicineBoxOutlined,
  ReloadOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { drugAPI } from '../services/api';
import type {
  DrugCapabilityResponse,
  DrugCatalogItem,
  DrugModelEvidenceStatus,
} from '../types';
import { useResponsive } from '../contexts/ResponsiveContext';
import DrugActivation from '../components/DrugActivation';
import AppDetailModal from '../components/AppDetailModal';
import ClinicalRouteIcon from '../components/ClinicalRouteIcon';
import PopulationIconGroup from '../components/PopulationIconGroup';
import './DrugList.css';

const { Title, Text, Link } = Typography;

const UNCATEGORIZED_LABEL = '未分类';

const ADMINISTRATION_MODE_LABELS: Record<string, string> = {
  intermittent: '静脉滴注',
  continuous: '连续输注',
  oral: '口服',
  bolus: '静脉注射',
};

const ADMINISTRATION_MODE_GLYPHS: Record<string, 'iv_infusion' | 'iv_injection' | 'continuous_infusion' | 'oral'> = {
  intermittent: 'iv_infusion',
  continuous: 'continuous_infusion',
  oral: 'oral',
  bolus: 'iv_injection',
};

const AdministrationModeList: React.FC<{
  modes: string[];
  compact?: boolean;
  showLabels?: boolean;
}> = ({ modes, compact = false, showLabels = false }) => (
  <div className="clinical-routes">
    {modes.length ? modes.map((mode) => {
      const label = ADMINISTRATION_MODE_LABELS[mode] || mode;
      const glyphCode = ADMINISTRATION_MODE_GLYPHS[mode];
      if (!glyphCode) return <Tag key={mode}>{label}</Tag>;
      return (
        <ClinicalRouteIcon
          key={mode}
          route={{ code: glyphCode === 'oral' ? 'oral' : 'other', label }}
          glyphCode={glyphCode}
          compact={compact}
          showLabel={showLabels}
        />
      );
    }) : <Text type="secondary">尚未配置</Text>}
  </div>
);

const EVIDENCE_STATUS: Record<DrugModelEvidenceStatus, { label: string; color?: string }> = {
  external_evidence_recorded: { label: '外部验证资料已登记', color: 'green' },
  validation_recorded: { label: '验证资料已登记', color: 'blue' },
  pending: { label: '验证资料待完善' },
  not_validated: { label: '未通过验证', color: 'red' },
};

const formatTarget = (target: DrugCatalogItem['targets'][number]) => {
  const hasMin = target.min !== null && target.min !== undefined;
  const hasMax = target.max !== null && target.max !== undefined;
  const range = hasMin || hasMax
    ? ` ${hasMin ? target.min : '—'}–${hasMax ? target.max : '—'}`
    : '';
  return `${target.parameter}${range}${target.unit ? ` ${target.unit}` : ''}`;
};

const ModelCapabilityCard: React.FC<{
  model: DrugCapabilityResponse['models'][number];
}> = ({ model }) => {
  const [showAllInputs, setShowAllInputs] = useState(false);
  const evidence = EVIDENCE_STATUS[model.evidence_status] || EVIDENCE_STATUS.pending;
  const visibleInputs = showAllInputs ? model.required_inputs : model.required_inputs.slice(0, 4);
  const hiddenCount = Math.max(model.required_inputs.length - visibleInputs.length, 0);
  const authorYear = [model.first_author, model.publication_year].filter(Boolean).join(' · ');

  return (
    <article className="drug-model">
      <div className="drug-model__header">
        <div>
          <div className="drug-model__title">{model.display_name}</div>
          {(authorYear || model.journal || model.doi || model.pmid) ? (
            <div className="drug-model__publication">
              <span>{[authorYear, model.journal].filter(Boolean).join(' · ')}</span>
              {model.doi ? (
                <Link href={`https://doi.org/${model.doi}`} target="_blank" rel="noreferrer">DOI</Link>
              ) : null}
              {model.pmid ? (
                <Link href={`https://pubmed.ncbi.nlm.nih.gov/${model.pmid}/`} target="_blank" rel="noreferrer">PubMed</Link>
              ) : null}
            </div>
          ) : null}
        </div>
        <Tag color={evidence.color}>{evidence.label}</Tag>
      </div>

      <div className="drug-model__row">
        <span className="drug-model__label">支持人群</span>
        <PopulationIconGroup populations={model.populations} detailed />
      </div>

      <div className="drug-model__row">
        <span className="drug-model__label">计算必需信息</span>
        <div>
          {visibleInputs.length ? (
            <div className="drug-model__inputs">
              {visibleInputs.map((input) => (
                <span className="drug-model__input" key={`${input.label}-${input.unit}`}>
                  {input.label}{input.unit ? `（${input.unit}）` : ''}
                </span>
              ))}
              {hiddenCount > 0 ? (
                <Button type="link" size="small" onClick={() => setShowAllInputs(true)}>
                  查看全部（{model.required_inputs.length}项）
                </Button>
              ) : null}
              {showAllInputs && model.required_inputs.length > 4 ? (
                <Button type="link" size="small" onClick={() => setShowAllInputs(false)}>收起</Button>
              ) : null}
            </div>
          ) : <Text type="secondary">暂无已登记的必需信息</Text>}
        </div>
      </div>
    </article>
  );
};

const DrugList: React.FC = () => {
  const { isMobileShell, isPhone } = useResponsive();
  const [loading, setLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(false);
  const [drugs, setDrugs] = useState<DrugCatalogItem[]>([]);
  const [multiDrug, setMultiDrug] = useState(false);
  const [maxDrugs, setMaxDrugs] = useState(0);
  const [activationVisible, setActivationVisible] = useState(false);
  const [selectedDrug, setSelectedDrug] = useState<DrugCatalogItem | null>(null);
  const [capability, setCapability] = useState<DrugCapabilityResponse | null>(null);
  const [capabilityLoading, setCapabilityLoading] = useState(false);
  const [capabilityError, setCapabilityError] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setCatalogError(false);
    try {
      const [catalogRes, statusRes] = await Promise.all([
        drugAPI.catalog(),
        drugAPI.activationStatus().catch(() => null),
      ]);
      setDrugs(catalogRes.drugs || []);
      if (statusRes) {
        setMultiDrug(Boolean(statusRes.multi_drug));
        setMaxDrugs(statusRes.max_drugs || 0);
      }
    } catch {
      setCatalogError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const loadCapability = async (drug: DrugCatalogItem) => {
    setSelectedDrug(drug);
    setCapability(null);
    setCapabilityError(false);
    setCapabilityLoading(true);
    try {
      const response = await drugAPI.capabilities(drug.drug_id);
      setCapability(response);
    } catch {
      setCapabilityError(true);
    } finally {
      setCapabilityLoading(false);
    }
  };

  const activatedCount = drugs.filter((drug) => drug.activated).length;
  const limitText = useMemo(() => {
    if (!multiDrug) return `${activatedCount}/1`;
    if (maxDrugs > 0) return `${activatedCount}/${maxDrugs}`;
    return `${activatedCount}`;
  }, [activatedCount, maxDrugs, multiDrug]);
  const quotaUsedUp = multiDrug
    ? maxDrugs > 0 && activatedCount >= maxDrugs
    : activatedCount >= 1;

  const groupedDrugs = useMemo(() => {
    const groups = new Map<string, DrugCatalogItem[]>();
    drugs.forEach((drug) => {
      const category = drug.category.trim() || UNCATEGORIZED_LABEL;
      groups.set(category, [...(groups.get(category) || []), drug]);
    });
    return Array.from(groups.entries())
      .sort(([left], [right]) => {
        if (left === UNCATEGORIZED_LABEL) return 1;
        if (right === UNCATEGORIZED_LABEL) return -1;
        return left.localeCompare(right, 'zh-CN');
      })
      .map(([category, items]) => ({
        key: category,
        label: category,
        drugs: items.sort((left, right) => left.drug_name.localeCompare(right.drug_name, 'zh-CN')),
      }));
  }, [drugs]);

  const renderDrugCard = (drug: DrugCatalogItem) => {
    const targets = drug.targets.map((target) => target.parameter).filter(Boolean).join(' / ') || '尚未配置';
    return (
      <Card
        key={drug.drug_id}
        hoverable
        className={`drug-catalog__card${drug.activated ? ' drug-catalog__card--activated' : ''}`}
        styles={{ body: { padding: '14px 16px' } }}
        tabIndex={0}
        role="button"
        aria-label={`查看${drug.drug_name}详情`}
        onClick={() => loadCapability(drug)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            loadCapability(drug);
          }
        }}
      >
        <Space direction="vertical" size={13} style={{ width: '100%' }}>
          <div className="drug-catalog__identity">
            <MedicineBoxOutlined style={{ marginTop: 3, color: '#0B2545' }} />
            <div className="drug-catalog__identity-copy">
              <div>
                <Text strong className="drug-catalog__name">{drug.drug_name}</Text>
              </div>
              <div className="drug-catalog__secondary-line">
                {drug.drug_name_en ? <span>{drug.drug_name_en}</span> : null}
                {drug.drug_name_en && drug.category ? <span>·</span> : null}
                {drug.category ? <span>{drug.category}</span> : null}
              </div>
            </div>
            {drug.activated ? (
              <span className="drug-catalog__status">
                <CheckCircleFilled />已激活
              </span>
            ) : null}
          </div>

          <div className="drug-catalog__facts">
            <div className="drug-catalog__fact">
              <span className="drug-catalog__fact-label">给药模式</span>
              <AdministrationModeList
                modes={drug.administration_modes}
                compact={!isPhone}
                showLabels={isPhone}
              />
            </div>
            <div className="drug-catalog__fact">
              <span className="drug-catalog__fact-label">适用人群</span>
              <PopulationIconGroup populations={drug.populations} />
            </div>
            <div className="drug-catalog__fact">
              <span className="drug-catalog__fact-label">支持目标</span>
              <div className="drug-catalog__targets" title={targets}>{targets}</div>
            </div>
          </div>

          <div className="drug-catalog__footer">
            <span>{drug.model_count > 0 ? `${drug.model_count} 个可用模型` : '当前工作区暂无可用模型'}</span>
            <span className="drug-catalog__footer-action">查看详情 <RightOutlined /></span>
          </div>
        </Space>
      </Card>
    );
  };

  return (
    <div className="drug-catalog">
      <div className="drug-catalog__header">
        <div>
          <Title level={4} style={{ margin: 0 }}>
            <MedicineBoxOutlined style={{ marginRight: 8 }} />支持药物
          </Title>
          <Text type="secondary">查看平台支持的药物、适用人群和治疗目标，按药理分类快速浏览。</Text>
        </div>
        <Button type="primary" onClick={() => setActivationVisible(true)}>管理激活药物</Button>
      </div>

      <Alert
        style={{ marginBottom: 16 }}
        type={quotaUsedUp ? 'warning' : 'info'}
        showIcon
        message={(
          <span>
            已激活 <Text strong>{limitText}</Text> 种药物。
            {quotaUsedUp
              ? ' 当前激活配额已用满，如需扩容请升级套餐。'
              : ' 可按当前套餐继续激活或调整药物。'}
          </span>
        )}
      />

      {loading ? (
        <Row gutter={[12, 12]}>
          {[0, 1, 2, 3].map((item) => (
            <Col key={item} xs={24} sm={12} xl={6}><Card><Skeleton active paragraph={{ rows: 5 }} /></Card></Col>
          ))}
        </Row>
      ) : catalogError ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="药物目录暂时无法加载"
        >
          <Button icon={<ReloadOutlined />} onClick={fetchData}>重新加载</Button>
        </Empty>
      ) : !drugs.length ? (
        <Empty description="平台暂未配置支持药物" />
      ) : (
        <Tabs
          items={groupedDrugs.map((group) => ({
            key: group.key,
            label: group.label,
            children: (
              <Row gutter={[12, 12]} align="stretch">
                {group.drugs.map((drug) => (
                  <Col key={drug.drug_id} xs={24} sm={12} lg={isMobileShell ? 12 : 8} xl={6} style={{ display: 'flex' }}>
                    <div style={{ width: '100%' }}>{renderDrugCard(drug)}</div>
                  </Col>
                ))}
              </Row>
            ),
          }))}
        />
      )}

      <DrugActivation
        visible={activationVisible}
        onClose={() => setActivationVisible(false)}
        onActivated={() => fetchData()}
      />

      <AppDetailModal
        open={Boolean(selectedDrug)}
        onClose={() => setSelectedDrug(null)}
        afterClose={() => {
          setCapability(null);
          setCapabilityError(false);
        }}
        title={selectedDrug ? (
          <div className="drug-capability__identity">
            <MedicineBoxOutlined style={{ marginTop: 4, color: '#0B2545' }} />
            <div className="drug-capability__identity-copy">
              <div>{selectedDrug.drug_name}</div>
              {selectedDrug.drug_name_en ? <Text type="secondary">{selectedDrug.drug_name_en}</Text> : null}
            </div>
          </div>
        ) : '药物详情'}
        subtitle={selectedDrug?.category || undefined}
        status={selectedDrug?.activated ? <Tag color="green">已激活</Tag> : <Tag>未激活</Tag>}
        footer={(
          <>
            <span className="drug-capability__disclaimer">
              以上为平台支持能力，不代表具体患者的模型推荐顺序。
            </span>
            <Button onClick={() => setSelectedDrug(null)}>关闭</Button>
            <Button
              type="primary"
              onClick={() => {
                setSelectedDrug(null);
                setActivationVisible(true);
              }}
            >
              管理药物激活
            </Button>
          </>
        )}
      >
        {capabilityLoading ? (
          <Skeleton active paragraph={{ rows: 10 }} />
        ) : capabilityError ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="药物能力详情暂时无法加载">
            {selectedDrug ? (
              <Button icon={<ReloadOutlined />} onClick={() => loadCapability(selectedDrug)}>重新加载</Button>
            ) : null}
          </Empty>
        ) : capability ? (
          <>
            <section className="drug-capability__summary" aria-label="药物概览">
              <div className="drug-capability__summary-item">
                <span className="drug-capability__summary-label">给药模式</span>
                <AdministrationModeList modes={capability.administration_modes} showLabels />
              </div>
              <div className="drug-capability__summary-item">
                <span className="drug-capability__summary-label">适用人群</span>
                <PopulationIconGroup populations={capability.populations} detailed />
              </div>
            </section>

            <section className="drug-capability__section">
              <div className="drug-capability__section-heading">
                <div className="drug-capability__section-title">治疗目标与计算能力</div>
                <Tag
                  className="drug-capability__remedial-status"
                  color={capability.supports_remedial_dosing ? 'success' : undefined}
                  icon={capability.supports_remedial_dosing ? <CheckCircleFilled /> : undefined}
                >
                  补救给药方案：{capability.supports_remedial_dosing ? '支持' : '暂不支持'}
                </Tag>
              </div>
              {capability.targets.length ? (
                <div className="drug-capability__target-list">
                  {capability.targets.map((target, index) => (
                    <div className="drug-capability__target" key={`${target.parameter}-${index}`}>
                      <div>
                        <Text strong>{target.method || target.parameter}</Text>
                        {target.method && target.method !== target.parameter ? (
                          <div><Text type="secondary">{target.parameter}</Text></div>
                        ) : null}
                      </div>
                      <div className="drug-capability__target-value">
                        <Text>{formatTarget(target)}</Text>
                        {target.administration_modes.length ? (
                          <span>适用：{target.administration_modes.map(
                            (mode) => ADMINISTRATION_MODE_LABELS[mode] || mode,
                          ).join('、')}</span>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="尚未配置治疗目标" />}
            </section>

            <section className="drug-capability__section">
              <div className="drug-capability__section-title">当前工作区支持的模型（{capability.models.length}）</div>
              {capability.models.length ? (
                <div className="drug-model-list">
                  {capability.models.map((model, index) => (
                    <ModelCapabilityCard key={`${model.display_name}-${index}`} model={model} />
                  ))}
                </div>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="当前工作区暂无可用模型"
                />
              )}
            </section>
          </>
        ) : null}
      </AppDetailModal>
    </div>
  );
};

export default DrugList;
